import { AuthConfig } from 'angular-oauth2-oidc';
import { CoreConfigurationService } from '@ftr/owa-core';
import { OAuthService } from 'angular-oauth2-oidc';

export interface OAuthPopupTokenPayload {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  scope?: string;
}

type OAuthServiceWithPkce = OAuthService & {
  saveNoncesInLocalStorage: boolean;
  redirectUri: string;
};

export const OAUTH_POPUP_COMPLETE_MESSAGE = 'mtpn-oauth-complete';

const OAUTH_STORAGE_KEYS = {
  accessToken: 'access_token',
  accessTokenStoredAt: 'access_token_stored_at',
  refreshToken: 'refresh_token',
  expiresAt: 'expires_at',
  idToken: 'id_token',
  idTokenStoredAt: 'id_token_stored_at',
  grantedScopes: 'granted_scopes'
} as const;

/** Must match a redirect URI registered in Azure AD. */
export function getOAuthRedirectUri(): string {
  return `${window.location.origin}/`;
}

export function configureOAuthService(
  oauthService: OAuthService,
  configurationService: CoreConfigurationService
): void {
  const authConfig = {
    ...configurationService.getConfiguration(AuthConfig.name),
    redirectUri: getOAuthRedirectUri()
  };
  const oauthWithPkce = oauthService as OAuthServiceWithPkce;
  oauthService.configure(authConfig);
  oauthWithPkce.saveNoncesInLocalStorage = true;
  oauthWithPkce.redirectUri = getOAuthRedirectUri();
}

export function clearOAuthCallbackParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('session_state');
  const search = url.searchParams.toString();
  history.replaceState(null, '', url.pathname + (search ? `?${search}` : ''));
}

export function buildTokenPayload(oauthService: OAuthService): OAuthPopupTokenPayload | null {
  if (!oauthService.hasValidAccessToken() && !oauthService.getAccessToken()) {
    return null;
  }

  const expiresAt = oauthService.getAccessTokenExpiration();
  const expiresIn = expiresAt
    ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
    : undefined;

  return {
    access_token: oauthService.getAccessToken(),
    refresh_token: oauthService.getRefreshToken() || undefined,
    id_token: oauthService.getIdToken() || undefined,
    expires_in: expiresIn
  };
}

export function applyPopupTokens(tokens: OAuthPopupTokenPayload): void {
  const now = Date.now();
  localStorage.setItem(OAUTH_STORAGE_KEYS.accessToken, tokens.access_token);
  localStorage.setItem(OAUTH_STORAGE_KEYS.accessTokenStoredAt, String(now));

  if (tokens.refresh_token) {
    localStorage.setItem(OAUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);
  }

  if (tokens.expires_in) {
    localStorage.setItem(OAUTH_STORAGE_KEYS.expiresAt, String(now + tokens.expires_in * 1000));
  }

  if (tokens.id_token) {
    localStorage.setItem(OAUTH_STORAGE_KEYS.idToken, tokens.id_token);
    localStorage.setItem(OAUTH_STORAGE_KEYS.idTokenStoredAt, String(now));
  }

  if (tokens.scope) {
    localStorage.setItem(OAUTH_STORAGE_KEYS.grantedScopes, tokens.scope);
  }
}

export function hasStoredAccessToken(): boolean {
  return !!localStorage.getItem(OAUTH_STORAGE_KEYS.accessToken);
}
